/**
 * Asset Revision API — Patch 28.
 *
 * Universal client for edit-anywhere, version history, AI rewrite,
 * republish, and the Marketing Intelligence Report. Every consumer
 * page (Content Review, Vault, Campaign Room, Publishing, Calendar,
 * Asset Detail) uses this client — never a bespoke one.
 */
import { http } from "@/shared/api/http";


export type RevisionAction =
  | "created" | "edited" | "ai_rewrite" | "approved"
  | "scheduled" | "published" | "republished" | "restored";
export type RevisionAuthor = "owner" | "ai" | "system";
export type RewriteAction =
  | "improve" | "shorten" | "expand"
  | "more_professional" | "more_casual"
  | "stronger_hook" | "stronger_cta"
  | "platform_optimize" | "regenerate_from_current";


export interface AssetRevision {
  revision_id: string;
  workspace_id: string;
  permanent_asset_id: string;
  version: number;
  parent_revision_id?: string | null;
  action: RevisionAction;
  author: RevisionAuthor;
  author_user_id?: string | null;
  title?: string | null;
  body_markdown?: string | null;
  status?: string | null;
  scheduled_at?: string | null;
  published_at?: string | null;
  ai_action?: string | null;
  ai_notes?: string | null;
  is_current: boolean;
  created_at: string;
}


export interface MarketingIntelligenceReport {
  objective: string;
  target_audience: string[];
  emotional_triggers: string[];
  primary_pains: string[];
  offer: { what: string; why_now: string; risk_reversal: string };
  cta: string;
  brand_voice: string[];
  creative_director_notes: string[];
  blueprint_reference: {
    blueprint_id: string;
    version: number;
    asset_type: string;
    target_platform: string;
  };
}


function base(ws: string, pid: string): string {
  return `/workspaces/${ws}/assets/${pid}`;
}

export async function saveEdit(
  ws: string, pid: string,
  input: { body_markdown?: string; title?: string; target_status?: string; scheduled_at?: string | null },
): Promise<AssetRevision> {
  return http<AssetRevision>(base(ws, pid), { method: "PATCH", body: input });
}

export async function listRevisions(ws: string, pid: string): Promise<AssetRevision[]> {
  return http<AssetRevision[]>(`${base(ws, pid)}/revisions`);
}

export async function restoreRevision(
  ws: string, pid: string, targetRevisionId: string,
): Promise<AssetRevision> {
  return http<AssetRevision>(`${base(ws, pid)}/revisions/restore`, {
    method: "POST", body: { target_revision_id: targetRevisionId },
  });
}

export async function diffRevisions(
  ws: string, pid: string, fromRevisionId: string, toRevisionId: string,
): Promise<{ from_version: number; to_version: number; unified_diff: string; changed_lines: number }> {
  return http(`${base(ws, pid)}/revisions/diff`, {
    method: "POST",
    body: { from_revision_id: fromRevisionId, to_revision_id: toRevisionId },
  });
}

export async function aiRewrite(
  ws: string, pid: string, action: RewriteAction,
): Promise<{ revision: AssetRevision; ai_action: RewriteAction; ai_notes: string }> {
  return http(`${base(ws, pid)}/ai-rewrite`, { method: "POST", body: { action } });
}

export async function republish(
  ws: string, pid: string, body_markdown: string,
): Promise<AssetRevision> {
  return http<AssetRevision>(`${base(ws, pid)}/republish`, {
    method: "POST", body: { body_markdown },
  });
}

export async function fetchMarketingIntelligence(
  ws: string, pid: string,
): Promise<MarketingIntelligenceReport> {
  return http<MarketingIntelligenceReport>(`${base(ws, pid)}/marketing-intelligence`);
}


// Human-readable labels for AI rewrite actions.
export const REWRITE_LABEL: Record<RewriteAction, string> = {
  improve: "Improve Writing",
  shorten: "Shorten",
  expand: "Expand",
  more_professional: "More Professional",
  more_casual: "More Casual",
  stronger_hook: "Stronger Hook",
  stronger_cta: "Stronger CTA",
  platform_optimize: "Platform Optimization",
  regenerate_from_current: "Regenerate From Current Draft",
};

export const REWRITE_GROUPS: Array<{ label: string; actions: RewriteAction[] }> = [
  { label: "General", actions: ["improve", "shorten", "expand"] },
  { label: "Tone", actions: ["more_professional", "more_casual"] },
  { label: "Impact", actions: ["stronger_hook", "stronger_cta"] },
  { label: "Platform", actions: ["platform_optimize", "regenerate_from_current"] },
];
